import {
  DvPreviewerToolPanelComponent,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabBase } from '@core/base/tab.base';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { ToolPanel } from '@pages/assets-page/components/interfaces';
import GridUtils from '@features/vt-grid/utils/grid.utils';
import { fileUploadModalConfig } from '@shared/components/file-upload-dialog/file-upload-dialog.config';
import { AttachmentsService } from '@shared/services/attachments.service';
import {
  GridApi,
  GridReadyEvent,
  IRowNode,
  SelectionChangedEvent,
  ToolPanelDef,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-community';
import { combineLatest, filter, take } from 'rxjs';
import { VehicleAttachmentDto } from './interfaces';
import { VehiclePictureDocumentGridService } from './vehicle-picture-document-grid.service';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { VehiclesTabsService } from '@pages/vehicles-page/services/vehicles-tabs.service';
import { VehicleType } from '@shared/constants/vehicle-type';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DialogService } from '@core/services/dialog.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { ActivatedRoute } from '@angular/router';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';

@Component({
  selector: 'app-vehicle-picture-document-tab',
  templateUrl: './vehicle-picture-document-tab.component.html',
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehiclePictureDocumentTabComponent
  extends TabBase
  implements OnInit
{
  private gridOptionsService = inject(GridOptionsService);
  private api = inject(RestApiService);
  private gridService = inject(VehiclePictureDocumentGridService);
  private dialog = inject(DialogService);
  private translate = inject(TranslateService);
  private snackBar = inject(DvSnackbarService);
  private attachmentService = inject(AttachmentsService);
  private vehicleInfoService = inject(VehiclesTabsService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private oidc = inject(OidcSecurityService);
  private entityService = inject(SplitterWithGridHelperService);

  gridOptions?: GridOptions;
  actions = signal<DvToolbarAction[]>([]);
  selectedNode: IRowNode<VehicleAttachmentDto> | undefined;
  entity: typeof Entity = Entity;

  private isToolPanelOpen: boolean;
  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);
  private isEntityDeleted: boolean;
  private isEditEnabled: boolean;

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.actions.set(
      this.gridService.getToolbarActions(this.uploadFile, this.deleteFile)
    );
    this.listenToEntityChanges();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;
    const vehicle = this.selectedNode?.data as VehicleAttachmentDto | undefined;
    this.toggleGridToolbarItems();

    if (vehicle?.DocumentID && this.isToolPanelOpen) {
      this.attachmentService.getAttachmentPreview(
        api!,
        vehicle.DocumentID,
        vehicle.FileName || ''
      );
    }
  }

  toolPanelVisibilityChanged(event: ToolPanelVisibleChangedEvent): void {
    this.isToolPanelOpen = event.api.isToolPanelShowing();
    const vehicleData = this.selectedNode?.data;

    if (this.selectedNode && vehicleData?.DocumentID && this.isToolPanelOpen) {
      this.attachmentService.getAttachmentPreview(
        this.gridApi!,
        vehicleData.DocumentID,
        vehicleData.FileName!
      );
    }
  }

  private standardPictureValueChangesFn = (selectedNodeId?: number): void => {
    this.api
      .upsert(Entity.Vehicle, this.entityId$.getValue(), {
        ImageDocumentID: selectedNodeId,
      })
      .pipe(take(1))
      .subscribe(_ => {
        this.entityService.refreshRowData(this.entityId$.getValue());
      });
  };

  private listenToEntityChanges(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(entityId => !!entityId)
      )
      .subscribe(entityId => {
        this.vehicleInfoService
          .getExtraVehicleData(entityId)
          .pipe(take(1))
          .subscribe(data => {
            this.isEntityDeleted = !data.ShowRecord;
            this.handleAccessRestrictions(
              data.VehicleTypeDescription,
              data.Username
            );
            this.toggleGridToolbarItems();
          });
        this.setGridOptions();
        this.setupGridToolPanel();

        (
          this.gridApi?.getToolPanelInstance('preview') as ToolPanel | undefined
        )?.api.setMediaType('');
      });
  }

  private toggleGridToolbarItems(): void {
    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled:
              !this.selectedNode || !this.isEditEnabled || this.isEntityDeleted,
          };
        }
        if (action.icon === 'download') {
          return {
            ...action,
            disabled:
              !this.selectedNode || !this.isEditEnabled || this.isEntityDeleted,
          };
        }
        if (action.icon === 'add_box') {
          return {
            ...action,
            disabled: !this.isEditEnabled || this.isEntityDeleted,
          };
        }
        return action;
      })
    );
  }

  private setupGridToolPanel(): void {
    GridUtils.addToolPanelIfMissing(
      this.gridOptions,
      {
        id: 'preview',
        labelDefault: 'Preview',
        labelKey: 'Preview',
        iconKey: 'preview',
        toolPanel: DvPreviewerToolPanelComponent,
        width: 400,
      } as ToolPanelDef,
      '<span class="dv-icons">eye</span>'
    );
  }

  private getFilterModel(filterId: string): FilterModelWithKeys {
    return {
      VehicleID: {
        filterType: 'number',
        type: 'equals',
        filter: filterId,
      },
    };
  }

  private uploadFile = (): void => {
    this.dialog
      .open(
        fileUploadModalConfig({
          entityId: this.entityId$.getValue(),
          url: `${environment.endpoints.restApi}/attachment/${
            Entity.Vehicle
          }/${this.entityId$.getValue()}`,
        })
      )
      .closed.pipe(take(1))
      .subscribe(({ responseData, source }) => {
        if (responseData && source === 'confirm') {
          this.gridOptions?.context.loadClientSideData?.(
            this.gridOptions.context.extraFilter,
            true
          );
        }
      });
  };

  private deleteFile = (event?: DvToolbarActionEvent): void => {
    this.attachmentService
      .openAttachmentDeleteDialog()
      .closed.pipe(take(1))
      .subscribe(({ source }) => {
        if (event && source === 'confirm') {
          const { api } = event;
          const colDefs = api.getColumns()?.map(c => c.getColDef());
          const { field } =
            colDefs?.find(
              c => c.type?.includes(ColumnType.Id) || c.type?.[0] === 'id'
            ) || {};
          if (!field) return;
          const selectedNodes: IRowNode[] = api.getSelectedNodes();
          if (!selectedNodes.length) {
            this.selectedNode = undefined;
            return;
          }
          const { data, rowIndex } = selectedNodes[0];

          this.api.deleteCustom(`Attachment/${data[field]}`).subscribe(() => {
            if (data.IsDefault) {
              this.entityService.refreshRowData(this.entityId$.getValue());
            }
            this.snackBar.open(
              this.translate.instant('General.DeleteActionSuccessful'),
              SnackBarConfig.Success
            );
            api.applyTransaction({ remove: [data!] });
            const lastIndex = api.getLastDisplayedRowIndex();
            const nextIndex = lastIndex > rowIndex! ? rowIndex! : lastIndex;
            api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
          });
        }
      });
  };

  private handleAccessRestrictions(
    vehicleType: VehicleType | undefined,
    defaultDriver: string | undefined
  ): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.VehicleEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
      this.oidc.getUserData(),
    ])
      .pipe(take(1))
      .subscribe(([hasEdit, hasEditMyPrivate, { email }]) => {
        if (
          hasEdit ||
          (hasEditMyPrivate &&
            vehicleType === VehicleType.Personal &&
            email.toLowerCase() === defaultDriver?.toLowerCase())
        ) {
          this.isEditEnabled = true;
        } else {
          this.isEditEnabled = false;
        }
      });
  }

  private setGridOptions(): void {
    const entityId = this.entityId$.getValue();
    if (!this.gridOptions) {
      this.gridOptions = this.gridOptionsService.clientSide(
        this.gridService.getColumnDefs(
          this.standardPictureValueChangesFn,
          () => this.isEditEnabled
        ),
        Entity.VehicleDocument,
        this.getFilterModel(this.entityId$.value)
      );
    } else {
      this.gridOptions.context.loadClientSideData?.(
        this.getFilterModel(entityId)
      );
    }
    this.gridOptions.context.extraFilter = this.getFilterModel(entityId);
  }
}
